<template>
  <div
    ref="select"
    v-clickoutside="handleClickOutside"
    class="search-filters-select"
  >
    <div
      v-if="!smallScreen"
      class="search-filters-select__desktop"
    >
      <div
        v-if="filter.options.length === 1"
        class="search-filters-select__single-option"
      >
        <label
          v-for="(option, i) in filter.options"
          :key="i"
          :class="['search-filters-select__option', { 'search-filters-select__option--disabled' : !option.active || isLoadingFilters }]"
        >
          <input
            ref="switchCheckbox"
            v-model="option.selected"
            :disabled="!option.active || isLoadingFilters"
            name="search-filter-option"
            type="checkbox"
            @change="check($event, option.id)"
          >
          <span class="checkmark" />
          {{ option.title }}
          ({{ option.count }})
        </label>
      </div>

      <div v-if="filter.options.length >= 1">
        <div :class="['search-filters-select__title', { 'search-filters-select__title--active': displayingPopup }]">
          {{ filterLabel }}
          <button
            :aria-expanded="displayingPopup ? 'true' : 'false'"
            :class="['search-filters-select__title-filter', { 'search-filters-select__title-filter--active' : displayingPopup }]"
            @click="togglePopup()"
          >
            {{ filter.title }}
            <svg
              :class="['icon', { 'icon--opened' : displayingPopup }]"
              role="img"
            >
              <title class="is-sr-only">{{ displayingPopup ? expandIconAlt : collapseIconAlt }}</title>
              <use xlink:href="#chevron-right" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="smallScreen"
      class="search-filters-select__mobile"
    >
      <accordion
        ref="accordion"
        custom-icon="chevron-right"
        :initial-display="initialDisplay"
        :title="filter.title"
        @toggled-accordion="toggledAccordion"
      >
        <div
          v-for="({ label, options }) in groupedOptions"
          :key="label"
          class="search-filters-select__group-list"
        >
          <span
            class="search-filters-select__title-group"
            v-text="label"
          />

          <label
            v-for="(option, i) in options"
            :key="i"
            :class="['search-filters-select__option', { 'search-filters-select__option--disabled' : !option.active || isLoadingFilters }]"
          >
            <input
              ref="switchCheckbox"
              v-model="option.selected"
              :disabled="!option.active || isLoadingFilters"
              name="search-filter-option"
              type="checkbox"
              @change="check($event, option.id)"
            >
            <span class="checkmark" />
            {{ option.title }}
            ({{ option.count }})
          </label>
        </div>
      </accordion>
    </div>

    <div
      v-if="!smallScreen"
      v-show="displayingPopup"
      ref="popup"
      :aria-hidden="displayingPopup ? 'false' : 'true'"
      class="search-filters-select__popup"
    >
      <div
        v-for="({ label, options }, groupIndex) in groupedOptions"
        :key="label"
        class="search-filters-select__group-list"
      >
        <span
          class="search-filters-select__title-group"
          v-text="label"
        />

        <label
          v-for="(option, i) in options"
          v-show="displayItem(i, groupIndex)"
          :key="i"
          :class="['search-filters-select__option', { 'search-filters-select__option--disabled' : !option.active || isLoadingFilters }]"
        >
          <input
            ref="switchCheckbox"
            v-model="option.selected"
            :disabled="!option.active || isLoadingFilters"
            name="search-filter-option"
            type="checkbox"
            @change="check($event, option.id)"
          >
          <span class="checkmark" />
          {{ option.title }}
          ({{ option.count }})
        </label>

        <span
          v-if="displayShowMoreButton(options.length, groupIndex)"
          class="search-filters-select__show-more-button"
          @click="showAllItems(groupIndex)"
          v-text="showMoreButtonLabel(options.length)"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { AnalyticsHandler } from '@Foundation/analyticsHandler';
  import { mapGetters } from 'vuex';
  import { eventBus } from '@Foundation/eventbus/eventbus';
  import Accordion from '@Feature/Accordion/code/Scripts/components/Accordion/Accordion.vue';
  import { clickoutside } from '@Foundation/directives/clickoutside/clickoutside';

  export default {
    name: 'SearchFiltersSelect',
    directives: {
      clickoutside
    },
    components: {
      Accordion
    },
    props: {
      closeOnOutsideClick: {
        default: true,
        type: Boolean,
      },
      filter: { type: Object, required: true },
      expandIconAlt: { type: String, required: false, default: 'Expand' },
      collapseIconAlt: { type: String, required: false, default: 'Collapse' },
      filterLabel: { type: String, required: false, default: 'Filter By:' },
      eventCategory: { type: String, required: false, default: 'Search' },
      isSearchPage: { type: Boolean, required: false, default: false },
      initialDisplay: {
        default: false,
        type: Boolean,
      },
      showMore: {
        default: () => ({
          label: 'Show ${a} more',
          limit: 10,
        }),
        type: Object,
      },
    },
    data() {
      return {
        smallScreen: !window.matchMedia('(min-width: 1280px)').matches,
        displayAllGroupItems: {},
        displayingPopup: false,
      };
    },
    computed: {
      ...mapGetters('searchFiltersStore', [
        'isLoadingFilters'
      ]),

      groupedOptions() {
        return this.filter.options.reduce((groups, option) => {
          const { group } = option;
          const existingGroup = groups.find((g) => g.label === group);

          if (existingGroup) {
            existingGroup.options.push(option);
            return groups;
          }

          groups.push({
            label: group,
            options: [option],
          });

          return groups;
        }, []);
      },

      taggingObject() {
        let label = '';
        this.filter.options.forEach(option => {
          if (option.selected) {
            label = `${label}${label.length ? '::' : ''}${option.title}`;
          }
        });
        if (this.isSearchPage) {
          return {
            event: 'uaevent',
            ecommerce: 'undefined',
            eventCategory: this.eventCategory,
            eventAction: `filter`,
            eventLabel: `${this.filter.title}::${label}`,
            event_name: 'use_filters',
            filter_type: 'filter',
            filter_value: `${this.filter.title}::${label}`,
          };
        }
        return {
          event: 'uaevent',
          ecommerce: 'undefined',
          eventCategory: this.filter.title,
          eventAction: `filter::${this.filter.title}`,
          eventLabel: label,
          event_name: 'use_filters',
          filter_type: 'filter',
          filter_value: `${this.filter.title}::${label}`,
        };
      }
    },
    mounted() {
      eventBus.$on('expanded-filter-accordion', this.handleExpandedAccordion);
      this.displayingPopup = this.initialDisplay;
    },
    methods: {
      check(event, optionId) {
        this.$emit(
          'selected-option',
          {
            filterId: this.filter.id,
            selectedOptionId: optionId,
            checked: event.target.checked
          }
        );
        this.sendTaggingEvent();
      },

      displayItem(index, groupId) {
        if (this.showMore.limit === 0 || this.displayAllGroupItems[groupId]) return true;

        return index < this.showMore.limit;
      },

      displayShowMoreButton(optionsLength, groupId) {
        const hasHiddenItems = optionsLength > this.showMore.limit;
        const hasValidLimit = this.showMore.limit > 0;
        const isActivated = this.displayAllGroupItems[groupId];

        return hasValidLimit && !isActivated && hasHiddenItems;
      },

      handleClickOutside() {
        this.closeOnOutsideClick && this.closePopup();
      },

      showAllItems(groupId) {
        this.$set(this.displayAllGroupItems, groupId, true);
      },

      showMoreButtonLabel(optionsLength) {
        const remainingItemsLength = optionsLength - this.showMore.limit;
        return this.showMore.label.replace('${a}', remainingItemsLength);
      },

      togglePopup() {
        this.displayingPopup = !this.displayingPopup;
      },
      closePopup() {
        this.displayingPopup = false;
      },
      handleExpandedAccordion(id) {
        if (this.filter.id !== id) {
          this.$refs.accordion.close();
        }
      },
      toggledAccordion(expanded) {
        if (expanded) {
          eventBus.$emit('expanded-filter-accordion', this.filter.id);
        }
      },
      sendTaggingEvent() {
        AnalyticsHandler.pushDataLayer(this.taggingObject);
      }
    }
  };
</script>
